import * as React from 'react'

import { useViewCount } from '@/hooks/use-view-count'

import { formatCount } from '@/utils/formatCount'

import { ViewCountEntityType, ViewStatsResponse } from '@/types/view-count.type'

interface ViewStatsDisplayProps {
  entityType: ViewCountEntityType
  entityId: string
  windowHours?: number
  showTotal?: boolean
  showUnique?: boolean
  showWindow?: boolean
  className?: string
  loadingText?: string
  errorText?: string
}

export const ViewStatsDisplay: React.FC<ViewStatsDisplayProps> = ({
  entityType,
  entityId,
  windowHours,
  showTotal = true,
  showUnique = true,
  showWindow = false,
  className = '',
  loadingText = 'Loading...',
  errorText = 'Failed to load stats',
}) => {
  const { getViewStats, isGettingStats, error, triggerSync } = useViewCount()
  const [stats, setStats] = React.useState<ViewStatsResponse | null>(null)

  React.useEffect(() => {
    const loadStats = async () => {
      const result = await getViewStats(entityType, entityId, windowHours)
      setStats(result)

      try {
        await triggerSync(entityType, entityId)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to trigger sync:', err)
      }
    }

    loadStats()
  }, [entityType, entityId, windowHours, getViewStats, triggerSync])

  if (isGettingStats) {
    return <span className={className}>{loadingText}</span>
  }

  if (error || !stats) {
    return <span className={className}>{errorText}</span>
  }

  const displayParts: string[] = []

  if (showTotal) {
    displayParts.push(`${formatCount(stats.totalViews)} total`)
  }

  if (showUnique) {
    displayParts.push(`${formatCount(stats.uniqueViews)} unique`)
  }

  if (showWindow && stats.viewsInWindow !== undefined) {
    displayParts.push(`${formatCount(stats.viewsInWindow)} in window`)
  }

  return <span className={className}>{displayParts.join(' • ')}</span>
}

export default ViewStatsDisplay
